import { FormEvent, useEffect, useState } from "react";
import AdminLayout from "layouts/AdminLayout";
import AdminCard from "components/admin/AdminCard";
import CustomTable, { ColumnConfig } from "components/CustomTable";
import Pagination from "components/Pagination";
import StatusBadge from "components/admin/StatusBadge";
import { Input } from "components/ui/input";
import { Button } from "components/ui/button";
import { Search, Download, AlertTriangle, FileText } from "lucide-react";
import InvoiceAjuste from "components/admin/InvoiceAjuste";
import {
  listAdminEnvios,
  reconciliarRastreios,
  ReconciliacaoResultado,
} from "services/admin/adminEnvios.service";

const formatUSD = (v: number) => v.toLocaleString("en-US", { style: "currency", currency: "USD" });
const formatBRL = (v: number) => v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const parseTrackings = (raw: string) =>
  raw
    .split(/[\n,;]+/)
    .map((t) => t.trim())
    .filter(Boolean);

const downloadCsv = (resultado: ReconciliacaoResultado) => {
  const header = ["rastreio", "encontrado", "cliente", "pedido", "transportadora", "status", "valor_usd", "valor_brl"];
  const lines = resultado.linhas.map((l) =>
    [
      l.tracking,
      l.encontrado ? "sim" : "nao",
      l.cliente || "",
      l.pedidoRef || "",
      l.carrier || "",
      l.status || "",
      l.valorUsd ?? "",
      l.valorBrl ?? "",
    ]
      .map((v) => `"${String(v).replace(/"/g, '""')}"`)
      .join(",")
  );
  const csv = [header.join(","), ...lines].join("\n");
  const blob = new Blob([`﻿${csv}`], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `reconciliacao-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

const STATUS_TONE: Record<string, "green" | "yellow" | "red" | "blue" | "slate"> = {
  ENTREGUE: "green",
  EM_TRANSITO: "blue",
  SAIU_PARA_ENTREGA: "blue",
  CRIADO: "slate",
  EXCECAO: "red",
  CANCELADO: "red",
  RETORNADO: "yellow",
  COLETADO: "blue",
};

type Row = {
  pedido: string;
  cliente: string;
  transportadora: string;
  tracking: string;
  pais: string;
  status: React.ReactNode;
  data: string;
  invoice: React.ReactNode;
};

const columns: ColumnConfig<Row>[] = [
  { key: "pedido", header: "Pedido" },
  { key: "cliente", header: "Cliente" },
  { key: "transportadora", header: "Transportadora" },
  { key: "tracking", header: "Rastreio" },
  { key: "pais", header: "País destino" },
  { key: "status", header: "Status" },
  { key: "data", header: "Data" },
  { key: "invoice", header: "Invoice" },
];

const AdminEnvios = () => {
  const [envios, setEnvios] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [carrier, setCarrier] = useState("");
  const [status, setStatus] = useState("");
  const [pais, setPais] = useState("");
  const [search, setSearch] = useState("");

  // Envio cuja invoice está sendo corrigida. `versao` força a lista a
  // recarregar depois de reemitir, para o botão refletir a correção.
  const [envioInvoice, setEnvioInvoice] = useState<any | null>(null);
  const [versao, setVersao] = useState(0);

  const [rastreioInput, setRastreioInput] = useState("");
  const [rastreioLoading, setRastreioLoading] = useState(false);
  const [rastreioError, setRastreioError] = useState<string | null>(null);
  const [resultado, setResultado] = useState<ReconciliacaoResultado | null>(null);

  useEffect(() => {
    setLoading(true);
    listAdminEnvios({ page, limit: 20, carrier, status, pais, search })
      .then((resp) => {
        setEnvios(resp.data);
        setTotalPages(resp.pagination.totalPages || 1);
      })
      .catch((err) => console.error("[AdminEnvios] erro:", err))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, carrier, status, pais, search, versao]);

  const handleBuscarRastreio = async (e: FormEvent) => {
    e.preventDefault();
    const trackings = parseTrackings(rastreioInput);
    if (!trackings.length) return;

    setRastreioLoading(true);
    setRastreioError(null);
    setResultado(null);
    try {
      const data = await reconciliarRastreios(trackings);
      setResultado(data);
    } catch (err: any) {
      setRastreioError(err?.response?.data?.error || "Falha ao buscar. Tente novamente.");
    } finally {
      setRastreioLoading(false);
    }
  };

  const rows: Row[] = envios.map((e) => ({
    pedido: e.pedido_ref,
    cliente: e.cliente?.razaoSocial || `Cliente ${e.cliente_id}`,
    transportadora: e.carrier,
    tracking: e.tracking_number || "—",
    pais: e.pais_dest || "—",
    status: <StatusBadge label={e.status_norm} tone={STATUS_TONE[e.status_norm] || "slate"} />,
    data: new Date(e.createdAt).toLocaleDateString("pt-BR"),
    // Corrigir valor ou descrição e reemitir o PDF. Fica aqui, e não na tela
    // do cliente, porque alterar documento aduaneiro é ação da Intrex.
    invoice: (
      <button
        type="button"
        onClick={() => setEnvioInvoice(e)}
        className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-[11px] font-medium text-slate-600 transition-colors hover:bg-slate-50"
      >
        <FileText className="h-3 w-3" />
        {e.invoice_ajustes ? "Corrigida" : "Corrigir"}
      </button>
    ),
  }));

  return (
    <AdminLayout title="Envios" subtitle="Todos os envios da plataforma">
      <div className="flex flex-col gap-4">
        <AdminCard title="Reconciliação de fatura por rastreio">
          <p className="text-xs text-slate-400 mb-3">
            Cole um ou vários números de rastreio (um por linha, ou separados por vírgula) de uma fatura da
            transportadora. Devolvemos de qual cliente é cada envio, o custo por cliente, e sinalizamos qualquer
            rastreio que não bate com nada no sistema.
          </p>
          <form onSubmit={handleBuscarRastreio} className="flex flex-col gap-2 sm:flex-row sm:items-start">
            <textarea
              placeholder={"1ZJE83726700733819\n1ZJE83726701314985\n..."}
              value={rastreioInput}
              onChange={(e) => setRastreioInput(e.target.value)}
              rows={3}
              className="flex w-full max-w-md rounded-md border border-input bg-background px-3 py-2 text-sm font-mono shadow-xs placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            />
            <Button type="submit" disabled={rastreioLoading || !rastreioInput.trim()}>
              <Search className="mr-2 h-4 w-4" />
              {rastreioLoading ? "Reconciliando..." : "Reconciliar"}
            </Button>
          </form>

          {rastreioError && <p className="mt-3 text-sm text-red-600">{rastreioError}</p>}

          {resultado && (
            <div className="mt-5 flex flex-col gap-5">
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-sm text-slate-600">
                  <strong className="text-slate-900">{resultado.totalEncontrados}</strong> de{" "}
                  <strong className="text-slate-900">{resultado.totalInformados}</strong> rastreios encontrados
                </span>
                {resultado.totalEncontrados < resultado.totalInformados && (
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-red-600">
                    <AlertTriangle className="h-3.5 w-3.5" />
                    {resultado.totalInformados - resultado.totalEncontrados} sem correspondência
                  </span>
                )}
                <Button variant="outline" size="sm" className="ml-auto" onClick={() => downloadCsv(resultado)}>
                  <Download className="mr-2 h-3.5 w-3.5" />
                  Baixar CSV
                </Button>
              </div>

              {resultado.resumo.length > 0 && (
                <div className="flex flex-wrap gap-3">
                  {resultado.resumo.map((r) => (
                    <div key={r.clienteId} className="rounded-lg border border-slate-200 px-4 py-3 min-w-[180px]">
                      <p className="text-sm font-semibold text-slate-900">{r.cliente || `Cliente ${r.clienteId}`}</p>
                      <p className="text-xs text-slate-400">
                        {r.envios} envio{r.envios === 1 ? "" : "s"}
                      </p>
                      <p className="mt-1 text-base font-semibold tabular-nums text-slate-900">
                        {formatUSD(r.totalUsd)}
                      </p>
                      {r.valorBrl != null && (
                        <p className="text-xs tabular-nums text-slate-400">{formatBRL(r.valorBrl)}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <div className="overflow-x-auto rounded-lg border border-slate-200">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-500">
                      <th className="px-3 py-2 font-medium">Rastreio</th>
                      <th className="px-3 py-2 font-medium">Cliente</th>
                      <th className="px-3 py-2 font-medium">Pedido</th>
                      <th className="px-3 py-2 font-medium">Transp.</th>
                      <th className="px-3 py-2 font-medium">Status</th>
                      <th className="px-3 py-2 text-right font-medium">Valor (USD)</th>
                      <th className="px-3 py-2 text-right font-medium">Valor (BRL)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {resultado.linhas.map((l) => (
                      <tr key={l.tracking} className={l.encontrado ? "" : "bg-red-50/60"}>
                        <td className="px-3 py-2 font-mono text-xs text-slate-700">{l.tracking}</td>
                        <td className="px-3 py-2 text-slate-800">
                          {l.encontrado ? (
                            l.cliente || `Cliente ${l.clienteId}`
                          ) : (
                            <span className="font-medium text-red-600">Não encontrado</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-slate-600">{l.pedidoRef || "—"}</td>
                        <td className="px-3 py-2 text-slate-600">{l.carrier || "—"}</td>
                        <td className="px-3 py-2">
                          {l.status ? (
                            <StatusBadge label={l.status} tone={STATUS_TONE[l.status] || "slate"} />
                          ) : (
                            "—"
                          )}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-slate-800">
                          {l.valorUsd != null ? formatUSD(l.valorUsd) : "—"}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-slate-500">
                          {l.valorBrl != null ? formatBRL(l.valorBrl) : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </AdminCard>

        <AdminCard noPadding className="flex flex-wrap gap-3 items-end p-4">
          <Input
            placeholder="Buscar por pedido ou rastreio"
            value={search}
            onChange={(e) => { setPage(1); setSearch(e.target.value); }}
            className="max-w-xs"
          />
          <select
            className="h-9 rounded-md border border-input bg-background px-3 text-sm"
            value={carrier}
            onChange={(e) => { setPage(1); setCarrier(e.target.value); }}
          >
            <option value="">Todas transportadoras</option>
            <option value="UPS">UPS</option>
            <option value="FEDEX">FedEx</option>
          </select>
          <select
            className="h-9 rounded-md border border-input bg-background px-3 text-sm"
            value={status}
            onChange={(e) => { setPage(1); setStatus(e.target.value); }}
          >
            <option value="">Todos status</option>
            <option value="CRIADO">Criado</option>
            <option value="COLETADO">Coletado</option>
            <option value="EM_TRANSITO">Em trânsito</option>
            <option value="SAIU_PARA_ENTREGA">Saiu para entrega</option>
            <option value="ENTREGUE">Entregue</option>
            <option value="EXCECAO">Exceção</option>
            <option value="CANCELADO">Cancelado</option>
            <option value="RETORNADO">Retornado</option>
          </select>
          <Input
            placeholder="País (código, ex: US)"
            value={pais}
            onChange={(e) => { setPage(1); setPais(e.target.value); }}
            className="max-w-[160px]"
          />
        </AdminCard>

        <AdminCard noPadding className="p-4">
          {loading ? (
            <p className="text-sm text-muted-foreground">Carregando...</p>
          ) : (
            <>
              <CustomTable objectList={rows} columns={columns} />
              <InvoiceAjuste
                envio={envioInvoice}
                onFechar={() => setEnvioInvoice(null)}
                onReemitido={() => setVersao((v) => v + 1)}
              />
              <Pagination page={page} totalPages={totalPages} onChange={setPage} />
            </>
          )}
        </AdminCard>
      </div>
    </AdminLayout>
  );
};

export default AdminEnvios;
